import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import ApiService from "../api/apiService";
import showSwal from "./swal";
import { convertTo12HourFormat } from "../util/helperFunctions";
import { common } from "../util/common";
import ModalShell from "./modal/ModalShell";
import UserAvatar from "./modal/UserAvatar";
import { EmptyState, SkeletonRows } from "./modal/EmptyState";

const PAGE_SIZE = 30;
const SCROLL_TARGET_ID = "view-activity-logs-scrollable-body";

// icon + tone per action; `group` drives the filter chips
const ACTION_META = {
  CREATE: { icon: "fa-plus-circle", tone: "ok", group: "members" },
  ADD: { icon: "fa-user-plus", tone: "ok", group: "members" },
  REMOVE: { icon: "fa-user-times", tone: "danger", group: "members" },
  LEAVE: { icon: "fa-sign-out", tone: "danger", group: "members" },
  MUTE: { icon: "fa-volume-off", tone: "warn", group: "members" },
  UPDATE: { icon: "fa-pencil", tone: "info", group: "settings" },
  PIN: { icon: "fa-thumb-tack", tone: "info", group: "messages" },
  DELETE: { icon: "fa-trash", tone: "danger", group: "messages" },
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "members", label: "Members" },
  { key: "messages", label: "Messages" },
  { key: "settings", label: "Settings" },
];

const dayLabel = (date) => {
  if (isNaN(date)) return "Unknown date";
  const startOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOf(new Date()) - startOf(date)) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

// receiving_user_action can be null for actions with no target (create, leave, update…)
function describe(item, userDetails) {
  const { user, action, receiving_user_action } = item;
  const sender = user?.id === userDetails?.id ? "You" : user?.display_name || "Someone";
  const receiver =
    receiving_user_action?.id === userDetails?.id ? "you" : receiving_user_action?.display_name || "a member";
  const s = <strong>{sender}</strong>;
  const r = <strong>{receiver}</strong>;

  switch (action) {
    case "MUTE":
      return <>{s} muted/unmuted {r}</>;
    case "ADD":
      return <>{s} added {r} to the chat</>;
    case "REMOVE":
      return <>{s} removed {r} from the chat</>;
    case "LEAVE":
      return <>{s} left the chat</>;
    case "DELETE":
      return <>{s} deleted a message</>;
    case "CREATE":
      return <>{s} created this group chat</>;
    case "UPDATE":
      return <>{s} updated the group chat</>;
    case "PIN":
      return <>{s} pinned/unpinned a message</>;
    default:
      return <>Unrecognized activity. If this keeps happening, please contact support.</>;
  }
}

function ViewActivityLogsModal({ conversationData, showHideViewActivityLogsModal, userDetails }) {
  const conversation_id = conversationData?.id;
  const conversation_name = conversationData?.name;

  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [counts, setCounts] = useState({ all: 0, members: 0, messages: 0, settings: 0 });
  // guards against out-of-order responses when the filter changes quickly
  const requestIdRef = useRef(0);

  const fetchPage = useCallback(
    async (pageToLoad, append) => {
      if (!conversation_id) return;
      const requestId = ++requestIdRef.current;
      try {
        if (append) setIsFetchingMore(true);
        else setLoading(true);
        const response = await ApiService.conversation.getConversationActionsV2(conversation_id, {
          page: pageToLoad,
          limit: PAGE_SIZE,
          group: filter,
        });
        if (requestId !== requestIdRef.current) return;
        if (!response?.success) return showSwal(common.errors.FETCH_ACTIVITY_LOGS_ERROR);
        const { conversation_actions = [], pagination, counts: serverCounts } = response;
        setActivityLogs((prev) => {
          if (!append) return conversation_actions;
          const seen = new Set(prev.map((l) => l.id));
          return [...prev, ...conversation_actions.filter((l) => !seen.has(l.id))];
        });
        setPage(pageToLoad);
        setHasMore(!!pagination?.hasNextPage);
        if (serverCounts) setCounts(serverCounts);
      } catch (err) {
        console.error(err);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setIsFetchingMore(false);
        }
      }
    },
    [conversation_id, filter]
  );

  // a new conversation or filter restarts from page 1
  useEffect(() => {
    fetchPage(1, false);
  }, [fetchPage]);

  const fetchMoreData = () => {
    if (!isFetchingMore && hasMore) fetchPage(page + 1, true);
  };

  // server already orders newest first; just bucket by day
  const groups = useMemo(() => {
    const sorted = activityLogs;
    const map = new Map();
    sorted.forEach((log) => {
      const label = dayLabel(new Date(log?.createdAt));
      if (!map.has(label)) map.set(label, []);
      map.get(label).push(log);
    });
    return Array.from(map.entries());
  }, [activityLogs]);

  const toolbar = (
    <div className='bh-filters' role='tablist'>
      {FILTERS.map((f) => (
        <button
          key={f.key}
          type='button'
          role='tab'
          aria-selected={filter === f.key}
          className={`bh-filter ${filter === f.key ? "is-active" : ""}`}
          onClick={() => setFilter(f.key)}
        >
          {f.label}
          <span className='bh-filter__count'>{counts[f.key]}</span>
        </button>
      ))}
    </div>
  );

  let content;
  if (loading) {
    content = <SkeletonRows count={5} />;
  } else if (groups.length === 0) {
    content = (
      <EmptyState
        icon='fa-history'
        title={counts.all === 0 ? "No activity yet" : "No activity in this category"}
        hint={counts.all === 0 ? "Member and group changes will be recorded here" : "Try another filter"}
      />
    );
  } else {
    const sections = groups.map(([label, logs]) => (
      <section key={label}>
        <h4 className='bh-month'>{label}</h4>
        <ul className='bh-timeline'>
          {logs.map((log) => {
            const meta = ACTION_META[log?.action] || { icon: "fa-question-circle", tone: "info" };
            return (
              <li key={log.id} className='bh-timeline__item'>
                <span className={`bh-timeline__icon bh-timeline__icon--${meta.tone}`}>
                  <i className={`fa ${meta.icon}`} aria-hidden='true'></i>
                </span>
                <div className='bh-row__main'>
                  <div className='bh-timeline__text'>{describe(log, userDetails)}</div>
                  <div className='bh-timeline__meta'>
                    <UserAvatar user={log?.user} size={16} />
                    <span>{convertTo12HourFormat(log?.createdAt)}</span>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </section>
    ));
    content = (
      <InfiniteScroll
        dataLength={activityLogs.length}
        next={fetchMoreData}
        hasMore={hasMore}
        scrollableTarget={SCROLL_TARGET_ID}
        scrollThreshold='180px'
        style={{ overflow: "visible" }}
        loader={<SkeletonRows count={2} />}
      >
        {sections}
      </InfiniteScroll>
    );
  }

  return (
    <ModalShell
      title='Activity log'
      subtitle={conversation_name}
      size='md'
      onClose={showHideViewActivityLogsModal}
      headerExtra={toolbar}
      bodyClassName='bh-modal__body--flush'
      bodyId={SCROLL_TARGET_ID}
    >
      {content}
    </ModalShell>
  );
}

export default ViewActivityLogsModal;
